import { DownloadOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { UploadRequestOption } from "rc-upload/lib/interface";
import { ReactElement, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { IApplicationDetailsDocSectionProps } from "../../../../../interfaces/props/IApplicationDetailsDocSectionProps";
import { IDownloadDocumentHandlerProps } from "../../../../../interfaces/props/IDownloadDocumentHandlerProps";
import { useUploadDocumentMutation } from "../../../../../store/api/applicationBankApi/documents.api";
import { useAppSelector } from "../../../../../store/reduxHooks";
import { SELECT_PROFILE_ROLE } from "../../../../../store/selectors/profileSelectors";
import { SELECT_REQUEST_BANK } from "../../../../../store/selectors/requestAppSelector";
import { DocumentToBank } from "../../../../../store/slice/applicationDataToBankSlice/applicationDataToBankSlice";
import { STATUS_APPLICATION, USER } from "../../../../../types/indexTypeEnum";
import { APPLICATION_BANK_STATUS_KKB } from "../../../../../utils/guards/ApplicationBankStatusGuard/types";
import { DownloadingFiles } from "../../../../../utils/utilities/Downloads";
import { validateFile } from "../../../../../utils/utilities/validateFile";
import DocCard from "../../../DocList/DocCard/DocCard";
import { TagSign } from "../TagSign/TagSign";

import classes from "./ApplicationDetailsDocSection.module.css";

export function ApplicationDetailsDocSection(
  props: IApplicationDetailsDocSectionProps,
): ReactElement {
  const { documents, section, applicationBankStatus } = props;
  const userRole = useAppSelector(SELECT_PROFILE_ROLE);
  const { applicationBankId, applicationId } = useParams();
  const data = useAppSelector(SELECT_REQUEST_BANK);

  const [errors, setErrors] = useState<string[]>([]);
  const [uploadDocument, result] = useUploadDocumentMutation();

  const isActive = [
    STATUS_APPLICATION.DRAFT,
    STATUS_APPLICATION.TO_REWORK,
  ].includes(data[0].status.type as STATUS_APPLICATION);

  function isDocumentUploading(
    documentId: string | number,
    memberPassportFlId?: string | number,
  ) {
    if (result?.isLoading) {
      if (
        memberPassportFlId &&
        result?.originalArgs?.body.get("member_passport_fl_id") ===
          memberPassportFlId.toString()
      ) {
        return true;
      }
      if (
        !memberPassportFlId &&
        result?.originalArgs?.body.get("type_doc_id") === "" + documentId
      ) {
        return true;
      }
    }

    return false;
  }

  function updateError(index: number, error: string) {
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = error;
      return newErrors;
    });
  }

  const uploadDocumentCallback = useCallback(
    (options: UploadRequestOption, index: number) => {
      const { file } = options;
      const { isValidate, error } = validateFile(file as File);
      if (isValidate) {
        const formFileData = new FormData();

        formFileData.append("file", file);
        formFileData.append("type_doc_id", `${documents[index]?.id}`);

        if (documents[index]?.member_passport_fl_id) {
          formFileData.append(
            "member_passport_fl_id",
            `${documents[index]?.member_passport_fl_id}`,
          );
        }

        if (applicationBankId) {
          uploadDocument({
            applicationBankId: Number(applicationBankId),
            applicationId: Number(applicationId),
            body: formFileData,
            section: section,
          })
            .then(() => {
              formFileData.delete("type_doc_id");
              formFileData.delete("file");
              formFileData.delete("member_passport_fl_id");
            })
            .finally(() => {
              formFileData.delete("type_doc_id");
              formFileData.delete("file");
              formFileData.delete("member_passport_fl_id");
            });
        }

        updateError(index, "");
      } else {
        updateError(index, error ? error : "");
      }
    },
    [documents, applicationBankId, section, uploadDocument],
  );

  const downloadDocumentCallback = useCallback(
    ({ name, document_id }: IDownloadDocumentHandlerProps) => {
      if (name && document_id && applicationBankId && isActive) {
        const urlFile = {
          url: `auth/applications/system-bank/create-data-to-bank/${applicationBankId}/file/${document_id}`,
          fileName: `${name}`,
        };
        DownloadingFiles(urlFile);
      }
    },
    [applicationBankId],
  );

  function docCardIcon({
    name,
    document_id,
  }: {
    name?: string;
    document_id?: number;
  }) {
    if (
      applicationBankStatus === APPLICATION_BANK_STATUS_KKB.OFFER &&
      userRole === USER.SUPPORT_CREDIT &&
      name
    ) {
      return (
        <Flex>
          <Button
            onClick={() => downloadDocumentCallback({ name, document_id })}
            variant="link"
            color="default"
            icon={<DownloadOutlined />}
            disabled={!isActive}
          ></Button>
        </Flex>
      );
    }

    return undefined;
  }

  return (
    <div className={classes.appDocSectionWrapper}>
      {Array.isArray(documents) &&
        documents?.map((document: DocumentToBank, index: number) => {
          return (
            <DocCard
              applicationBankStatus={applicationBankStatus}
              key={"" + document.id + document.member_id}
              item={{
                ...document,
                document: document.document ?? null,
              }}
              Tag={
                document.document && (
                  <TagSign
                    tag={
                      !!document.document?.sign_file?.length
                        ? "sign_true"
                        : "sign_false"
                    }
                  />
                )
              }
              isActive={isActive}
              index={index}
              error={errors[index]}
              isUploadingDocument={isDocumentUploading(
                document.id ?? "",
                document.member_passport_fl_id,
              )}
              uploadDocumentCallback={uploadDocumentCallback}
              downloadDocumentCallback={downloadDocumentCallback}
              icon={docCardIcon({
                name: document.document?.name,
                document_id: document.document?.id,
              })}
            />
          );
        })}
    </div>
  );
}
